import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Group,
  Loader,
  Paper,
  Stack,
  Table,
  TextInput,
} from '@mantine/core';
import { IconEdit, IconPlus, IconSearch } from '@tabler/icons-react';
import { PageHeader } from '../../components/PageHeader';
import { useSuppliers } from './purchasingApi';
import { SupplierFormDrawer } from './SupplierFormDrawer';
import type { SupplierDto } from './types';

export function SuppliersPage() {
  const { t } = useTranslation();
  const { data, isLoading, error } = useSuppliers();
  const [search, setSearch] = useState('');
  const [editing, setEditing] = useState<SupplierDto | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const filtered = useMemo(() => {
    if (!data) return [];
    const q = search.trim().toLowerCase();
    if (!q) return data;
    return data.filter(
      (s) =>
        s.nameAr.toLowerCase().includes(q) ||
        s.nameEn?.toLowerCase().includes(q) ||
        s.contactName?.toLowerCase().includes(q) ||
        s.contactPhone?.toLowerCase().includes(q),
    );
  }, [data, search]);

  return (
    <Stack gap="md">
      <PageHeader
        title={t('nav.items.suppliers')}
        actions={
          <Button
            color="wheels"
            leftSection={<IconPlus size={16} stroke={1.6} />}
            onClick={() => {
              setEditing(null);
              setDrawerOpen(true);
            }}
          >
            {t('purchasing.supplier.create')}
          </Button>
        }
      />

      <Paper p="md" withBorder>
        <TextInput
          leftSection={<IconSearch size={16} stroke={1.6} />}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          placeholder={t('purchasing.supplier.searchPlaceholder') ?? ''}
          maw={400}
        />
      </Paper>

      {isLoading && <Group justify="center" py="xl"><Loader size="sm" /></Group>}
      {error && (
        <Alert color="red">
          {error instanceof Error ? error.message : t('purchasing.supplier.loadFailed')}
        </Alert>
      )}
      {data && data.length === 0 && !isLoading && (
        <Alert color="gray">{t('purchasing.supplier.empty')}</Alert>
      )}

      {filtered.length > 0 && (
        <Paper withBorder>
          <Table striped highlightOnHover>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('purchasing.supplier.nameAr')}</Table.Th>
                <Table.Th>{t('purchasing.supplier.contactName')}</Table.Th>
                <Table.Th>{t('purchasing.supplier.contactPhone')}</Table.Th>
                <Table.Th>{t('purchasing.supplier.status')}</Table.Th>
                <Table.Th />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {filtered.map((s) => (
                <Table.Tr key={s.supplierId}>
                  <Table.Td>{s.nameAr}</Table.Td>
                  <Table.Td>{s.contactName ?? '—'}</Table.Td>
                  <Table.Td dir="ltr">{s.contactPhone ?? '—'}</Table.Td>
                  <Table.Td>
                    <Badge color={s.isActive ? 'green' : 'gray'} variant="light">
                      {s.isActive ? t('purchasing.supplier.active') : t('purchasing.supplier.inactive')}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Group gap={4} justify="flex-end">
                      <ActionIcon
                        variant="subtle"
                        onClick={() => {
                          setEditing(s);
                          setDrawerOpen(true);
                        }}
                      >
                        <IconEdit size={18} stroke={1.6} />
                      </ActionIcon>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Paper>
      )}

      <SupplierFormDrawer
        supplier={editing}
        opened={drawerOpen}
        onClose={() => setDrawerOpen(false)}
      />
    </Stack>
  );
}
